/**
 * Error Tracking Hooks
 * 
 * Client-side error capture and reporting:
 * - Error boundary wrapper
 * - Automatic error capture
 * - Breadcrumb tracking
 * - Performance monitoring
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import React from 'react';

const API_BASE = '/api';

// ============================================================================
// Types
// ============================================================================

export type ErrorSeverity = 'debug' | 'info' | 'warning' | 'error' | 'fatal';
export type ErrorStatus = 'unresolved' | 'ignored' | 'resolved' | 'muted';

export interface StackFrame {
  filename?: string;
  function?: string;
  lineno?: number;
  colno?: number;
  abs_path?: string;
  context_line?: string;
  in_app?: boolean;
}

export interface Breadcrumb {
  timestamp: string;
  type: 'navigation' | 'click' | 'xhr' | 'fetch' | 'console' | 'error' | 'user';
  category?: string;
  message?: string;
  data?: Record<string, unknown>;
  level?: string;
}

export interface ErrorEvent {
  event_id: string;
  message: string;
  type?: string;
  stack_trace?: string;
  parsed_stack?: StackFrame[];
  severity?: ErrorSeverity;
  url?: string;
  user_agent?: string;
  user_id?: string;
  session_id?: string;
  environment?: string;
  release?: string;
  browser_name?: string;
  browser_version?: string;
  os_name?: string;
  os_version?: string;
  device_type?: string;
  breadcrumbs?: Breadcrumb[];
  extra_data?: Record<string, unknown>;
  tags?: Record<string, string>;
  culprit?: string;
}

export interface ErrorFingerprint {
  id: string;
  tenant_id: string;
  fingerprint: string;
  title: string;
  culprit?: string;
  type?: string;
  message?: string;
  status: ErrorStatus;
  severity: ErrorSeverity;
  first_seen_at: string;
  last_seen_at: string;
  occurrence_count: number;
  user_count: number;
  assigned_to?: string;
  resolved_at?: string;
  tags: string[];
  is_regression: boolean;
}

export interface ErrorOccurrence {
  id: string;
  fingerprint_id: string;
  event_id: string;
  message: string;
  stack_trace?: string;
  url?: string;
  user_agent?: string;
  environment: string;
  release?: string;
  browser_name?: string;
  os_name?: string;
  device_type?: string;
  breadcrumbs?: Breadcrumb[];
  created_at: string;
}

export interface ErrorStats {
  total_errors: number;
  unresolved_count: number;
  resolved_today: number;
  new_today: number;
  affected_users_today: number;
  by_severity: Record<string, number>;
  by_environment: Record<string, number>;
  top_errors: Array<{
    id: string;
    title: string;
    occurrence_count: number;
    user_count: number;
    severity: ErrorSeverity;
  }>;
}

export interface ErrorClientConfig {
  dsn?: string;
  environment?: string;
  release?: string;
  sampleRate?: number;
  maxBreadcrumbs?: number;
  beforeSend?: (event: ErrorEvent) => ErrorEvent | null;
  ignoreErrors?: (string | RegExp)[];
  denyUrls?: (string | RegExp)[];
}

// ============================================================================
// Utilities
// ============================================================================

function generateEventId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function generateSessionId(): string {
  const stored = sessionStorage.getItem('error_session_id');
  if (stored) return stored;
  const id = generateEventId();
  sessionStorage.setItem('error_session_id', id);
  return id;
}

function parseUserAgent(): {
  browser_name?: string;
  browser_version?: string;
  os_name?: string;
  os_version?: string;
  device_type?: string;
} {
  const ua = navigator.userAgent;
  let browser_name: string | undefined;
  let browser_version: string | undefined;
  let os_name: string | undefined;
  let os_version: string | undefined;
  let device_type: string | undefined = 'desktop';

  // Browser detection
  if (ua.includes('Chrome')) {
    browser_name = 'Chrome';
    browser_version = ua.match(/Chrome\/(\d+)/)?.[1];
  } else if (ua.includes('Firefox')) {
    browser_name = 'Firefox';
    browser_version = ua.match(/Firefox\/(\d+)/)?.[1];
  } else if (ua.includes('Safari')) {
    browser_name = 'Safari';
    browser_version = ua.match(/Version\/(\d+)/)?.[1];
  }

  // OS detection
  if (ua.includes('Windows')) {
    os_name = 'Windows';
    os_version = ua.match(/Windows NT (\d+\.\d+)/)?.[1];
  } else if (ua.includes('Mac OS')) {
    os_name = 'macOS';
    os_version = ua.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace('_', '.');
  } else if (ua.includes('Linux')) {
    os_name = 'Linux';
  }

  // Device type
  if (/Mobile|Android|iPhone/.test(ua)) {
    device_type = 'mobile';
  } else if (/Tablet|iPad/.test(ua)) {
    device_type = 'tablet';
  }

  return { browser_name, browser_version, os_name, os_version, device_type };
}

function parseStackTrace(error: Error): StackFrame[] {
  if (!error.stack) return [];
  
  const lines = error.stack.split('\n').slice(1);
  return lines.map((line) => {
    const match = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/) ||
                  line.match(/at\s+(.+?):(\d+):(\d+)/);
    
    if (!match) return { filename: line };
    
    if (match.length === 5) {
      return {
        function: match[1],
        filename: match[2] || '',
        lineno: parseInt(match[3] || '0', 10),
        colno: parseInt(match[4] || '0', 10),
        in_app: !(match[2] || '').includes('node_modules'),
      };
    }
    
    return {
      filename: match[1] || '',
      lineno: parseInt(match[2] || '0', 10),
      colno: parseInt(match[3] || '0', 10),
      in_app: !(match[1] || '').includes('node_modules'),
    };
  }).filter((frame) => frame.filename);
}

function shouldIgnoreError(
  message: string,
  ignorePatterns: (string | RegExp)[] = []
): boolean {
  return ignorePatterns.some((pattern) => {
    if (typeof pattern === 'string') {
      return message.includes(pattern);
    }
    return pattern.test(message);
  });
}

// ============================================================================
// Error Client Singleton
// ============================================================================

class ErrorClient {
  private static instance: ErrorClient;
  private config: ErrorClientConfig = {};
  private breadcrumbs: Breadcrumb[] = [];
  private userId?: string;
  private sessionId: string;
  private initialized = false;

  private constructor() {
    this.sessionId = generateSessionId();
  }

  static getInstance(): ErrorClient {
    if (!ErrorClient.instance) {
      ErrorClient.instance = new ErrorClient();
    }
    return ErrorClient.instance;
  }

  init(config: ErrorClientConfig) {
    this.config = {
      environment: 'production',
      sampleRate: 1.0,
      maxBreadcrumbs: 100,
      ...config,
    };
    
    if (!this.initialized) {
      this.setupGlobalHandlers();
      this.initialized = true;
    }
  }

  setUser(userId: string) {
    this.userId = userId;
  }

  clearUser() {
    this.userId = undefined;
  }

  addBreadcrumb(breadcrumb: Omit<Breadcrumb, 'timestamp'>) {
    const crumb: Breadcrumb = {
      ...breadcrumb,
      timestamp: new Date().toISOString(),
    };
    
    this.breadcrumbs.push(crumb);
    
    const maxCrumbs = this.config.maxBreadcrumbs || 100;
    if (this.breadcrumbs.length > maxCrumbs) {
      this.breadcrumbs = this.breadcrumbs.slice(-maxCrumbs);
    }
  }

  async captureException(
    error: Error,
    extras?: { tags?: Record<string, string>; extra?: Record<string, unknown> }
  ): Promise<string | null> {
    // Sample rate check
    if (Math.random() > (this.config.sampleRate || 1)) {
      return null;
    }

    // Ignore check
    if (shouldIgnoreError(error.message, this.config.ignoreErrors)) {
      return null;
    }

    const ua = parseUserAgent();
    const eventId = generateEventId();

    let event: ErrorEvent = {
      event_id: eventId,
      message: error.message,
      type: error.name,
      stack_trace: error.stack,
      parsed_stack: parseStackTrace(error),
      severity: 'error',
      url: window.location.href,
      user_agent: navigator.userAgent,
      user_id: this.userId,
      session_id: this.sessionId,
      environment: this.config.environment,
      release: this.config.release,
      ...ua,
      breadcrumbs: [...this.breadcrumbs],
      extra_data: extras?.extra,
      tags: extras?.tags,
      culprit: this.extractCulprit(error),
    };

    // Apply beforeSend hook
    if (this.config.beforeSend) {
      const modified = this.config.beforeSend(event);
      if (!modified) return null;
      event = modified;
    }

    try {
      await fetch(`${API_BASE}/errors/ingest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
      });
      return eventId;
    } catch {
      console.error('Failed to report error to server');
      return null;
    }
  }

  async captureMessage(
    message: string,
    severity: ErrorSeverity = 'info',
    extras?: { tags?: Record<string, string>; extra?: Record<string, unknown> }
  ): Promise<string | null> {
    const ua = parseUserAgent();
    const eventId = generateEventId();

    const event: ErrorEvent = {
      event_id: eventId,
      message,
      severity,
      url: window.location.href,
      user_agent: navigator.userAgent,
      user_id: this.userId,
      session_id: this.sessionId,
      environment: this.config.environment,
      release: this.config.release,
      ...ua,
      breadcrumbs: [...this.breadcrumbs],
      extra_data: extras?.extra,
      tags: extras?.tags,
    };

    try {
      await fetch(`${API_BASE}/errors/ingest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
      });
      return eventId;
    } catch {
      return null;
    }
  }

  private extractCulprit(error: Error): string | undefined {
    const frames = parseStackTrace(error);
    const appFrame = frames.find((f) => f.in_app);
    if (appFrame) {
      return appFrame.function || appFrame.filename;
    }
    return frames[0]?.function || frames[0]?.filename;
  }

  private setupGlobalHandlers() {
    // Capture unhandled errors
    window.addEventListener('error', (event) => {
      this.captureException(event.error || new Error(event.message));
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));
      this.captureException(error);
    });

    // Track navigation
    const originalPushState = history.pushState;
    history.pushState = (...args) => {
      this.addBreadcrumb({
        type: 'navigation',
        category: 'navigation',
        message: `Navigated to ${args[2]}`,
      });
      return originalPushState.apply(history, args);
    };

    // Track clicks
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'BUTTON' || target.tagName === 'A') {
        this.addBreadcrumb({
          type: 'click',
          category: 'ui',
          message: `Clicked ${target.tagName.toLowerCase()}`,
          data: {
            text: target.textContent?.slice(0, 100),
            id: target.id,
            className: target.className,
          },
        });
      }
    }, true);

    // Track fetch requests
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;
      const method = (args[1]?.method || 'GET').toUpperCase();
      
      try {
        const response = await originalFetch.apply(window, args);
        this.addBreadcrumb({
          type: 'fetch',
          category: 'http',
          message: `${method} ${url}`,
          data: { status: response.status },
          level: response.ok ? 'info' : 'error',
        });
        return response;
      } catch (error) {
        this.addBreadcrumb({
          type: 'fetch',
          category: 'http',
          message: `${method} ${url}`,
          data: { error: (error as Error).message },
          level: 'error',
        });
        throw error;
      }
    };

    // Track console errors
    const originalConsoleError = console.error;
    console.error = (...args) => {
      this.addBreadcrumb({
        type: 'console',
        category: 'console',
        message: args.map((a) => String(a)).join(' '),
        level: 'error',
      });
      return originalConsoleError.apply(console, args);
    };
  }
}

// ============================================================================
// React Hooks
// ============================================================================

/**
 * Initialize error tracking client
 */
export function useErrorTracking(config: ErrorClientConfig) {
  useEffect(() => {
    const client = ErrorClient.getInstance();
    client.init(config);
  }, [config]);

  const setUser = useCallback((userId: string) => {
    ErrorClient.getInstance().setUser(userId);
  }, []);

  const clearUser = useCallback(() => {
    ErrorClient.getInstance().clearUser();
  }, []);

  const captureException = useCallback(
    (error: Error, extras?: { tags?: Record<string, string>; extra?: Record<string, unknown> }) => {
      return ErrorClient.getInstance().captureException(error, extras);
    },
    []
  );

  const captureMessage = useCallback(
    (message: string, severity?: ErrorSeverity, extras?: { tags?: Record<string, string>; extra?: Record<string, unknown> }) => {
      return ErrorClient.getInstance().captureMessage(message, severity, extras);
    },
    []
  );

  const addBreadcrumb = useCallback((breadcrumb: Omit<Breadcrumb, 'timestamp'>) => {
    ErrorClient.getInstance().addBreadcrumb(breadcrumb);
  }, []);

  return {
    setUser,
    clearUser,
    captureException,
    captureMessage,
    addBreadcrumb,
  };
}

/**
 * Query errors from the API
 */
export function useErrors(params?: {
  status?: string;
  severity?: string;
  environment?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  const [errors, setErrors] = useState<ErrorFingerprint[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchErrors = useCallback(async () => {
    try {
      setIsLoading(true);
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.append('status', params.status);
      if (params?.severity) searchParams.append('severity', params.severity);
      if (params?.environment) searchParams.append('environment', params.environment);
      if (params?.search) searchParams.append('search', params.search);
      if (params?.page) searchParams.append('page', params.page.toString());
      if (params?.limit) searchParams.append('limit', params.limit.toString());

      const response = await fetch(`${API_BASE}/errors?${searchParams}`);
      if (!response.ok) throw new Error('Failed to fetch errors');
      const data = await response.json();
      setErrors(data.errors);
      setTotal(data.total);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [params?.status, params?.severity, params?.environment, params?.search, params?.page, params?.limit]);

  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  const unresolvedErrors = useMemo(
    () => errors.filter((e) => e.status === 'unresolved'),
    [errors]
  );

  return {
    errors,
    unresolvedErrors,
    total,
    isLoading,
    error,
    refresh: fetchErrors,
  };
}

/**
 * Get error details
 */
export function useError(errorId: string | null) {
  const [error, setError] = useState<ErrorFingerprint | null>(null);
  const [occurrences, setOccurrences] = useState<ErrorOccurrence[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchError_ = useCallback(async () => {
    if (!errorId) return;
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}/errors/${errorId}`);
      if (!response.ok) throw new Error('Failed to fetch error');
      const data = await response.json();
      setError(data.fingerprint);
      setOccurrences(data.recent_occurrences);
      setFetchError(null);
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [errorId]);

  useEffect(() => {
    fetchError_();
  }, [fetchError_]);

  const updateError = useCallback(
    async (updates: { status?: ErrorStatus; severity?: ErrorSeverity; assigned_to?: string }) => {
      if (!errorId) throw new Error('No error ID');
      const response = await fetch(`${API_BASE}/errors/${errorId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!response.ok) throw new Error('Failed to update error');
      const data = await response.json();
      setError((prev) => (prev ? { ...prev, ...data } : null));
      return data;
    },
    [errorId]
  );

  const resolveError = useCallback(
    async (notes?: string) => {
      if (!errorId) throw new Error('No error ID');
      const response = await fetch(`${API_BASE}/errors/${errorId}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(notes),
      });
      if (!response.ok) throw new Error('Failed to resolve error');
      const data = await response.json();
      setError((prev) => (prev ? { ...prev, status: 'resolved' } : null));
      return data;
    },
    [errorId]
  );

  const ignoreError = useCallback(async () => {
    if (!errorId) throw new Error('No error ID');
    const response = await fetch(`${API_BASE}/errors/${errorId}/ignore`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Failed to ignore error');
    const data = await response.json();
    setError((prev) => (prev ? { ...prev, status: 'ignored' } : null));
    return data;
  }, [errorId]);

  return {
    error,
    occurrences,
    isLoading,
    fetchError,
    refresh: fetchError_,
    updateError,
    resolveError,
    ignoreError,
  };
}

/**
 * Get error statistics
 */
export function useErrorStats() {
  const [stats, setStats] = useState<ErrorStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}/errors/stats`);
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, isLoading, error, refresh: fetchStats };
}

// ============================================================================
// Error Boundary Component
// ============================================================================

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode | ((error: Error, reset: () => void) => React.ReactNode);
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Report to error tracking
    ErrorClient.getInstance().captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });

    // Call custom handler if provided
    this.props.onError?.(error, errorInfo);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(this.state.error, this.reset);
      }
      
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="p-5 text-center">
          <h2>Something went wrong</h2>
          <p>{this.state.error.message}</p>
          <button onClick={this.reset}>Try again</button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// Helper Hook for Scoped Error Capture
// ============================================================================

/**
 * Create a try-catch wrapper that reports errors
 */
export function useErrorHandler() {
  const handleError = useCallback(
    <T,>(
      fn: () => T | Promise<T>,
      fallback?: T
    ): T | Promise<T> => {
      try {
        const result = fn();
        if (result instanceof Promise) {
          return result.catch((error) => {
            ErrorClient.getInstance().captureException(error);
            if (fallback !== undefined) return fallback;
            throw error;
          });
        }
        return result;
      } catch (error) {
        ErrorClient.getInstance().captureException(error as Error);
        if (fallback !== undefined) return fallback;
        throw error;
      }
    },
    []
  );

  return handleError;
}

/**
 * Wrap async operations with error reporting
 */
export function useAsyncErrorHandler<T extends (...args: unknown[]) => Promise<unknown>>(
  asyncFn: T
): T {
  return useCallback(
    (async (...args: Parameters<T>) => {
      try {
        return await asyncFn(...args);
      } catch (error) {
        await ErrorClient.getInstance().captureException(error as Error);
        throw error;
      }
    }) as T,
    [asyncFn]
  );
}
